import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Chip,
  LinearProgress,
  Link,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import moment from "moment";
import NoData from "../../Resources/Images/NoDataNavy.png";
import { formatINR } from "../AddMoneyToUser/addMoneyUtils";
import { outlinedButtonSx } from "../AddMoneyToUser/parts";
import Styles from "../AddMoneyToUser/AddMoneyToUser.module.scss";
import {
  TONE_COLORS,
  canOpenPolicy,
  describeRow,
  formatSigned,
  typeInfo,
  userWalletNotice,
  walletWarnings,
} from "./walletStatementUtils";

const COLUMNS = ["Date", "Type", "Details", "Amount", "Balance after"];
const PAGE_SIZES = [10, 25, 50, 100];

// The same statement is shown to the admin (one user, in a dialog) and to the user (their own page);
// only the wording differs.
const COPY = {
  admin: {
    balanceLabel: "Balance",
    balanceHint: "Available to spend",
    pendingLabel: "Pending",
    emptyText: "Top-ups, policy debits and refunds for this wallet will be listed here.",
  },
  user: {
    balanceLabel: "Available Balance",
    balanceHint: "You can spend this",
    pendingLabel: "Pending Approval",
    emptyText: "Your top-ups, policy debits and refunds will be listed here.",
  },
};

const Dash = () => <span style={{ color: "#98A2B3" }}>—</span>;

const TypeChip = ({ type }) => {
  const info = typeInfo(type);
  const tone = TONE_COLORS[info.tone];
  return (
    <Chip
      size="small"
      label={info.label}
      sx={{
        height: 22,
        fontFamily: "Inter",
        fontSize: "11.5px",
        fontWeight: 500,
        color: tone.color,
        backgroundColor: tone.bg,
        border: `1px solid ${tone.border}`,
      }}
    />
  );
};

const FigureCard = ({ label, value, hint, color = "#101828" }) => (
  <Box
    role="group"
    aria-label={label}
    sx={{
      flex: "1 1 190px",
      minWidth: 0,
      p: "12px 14px",
      border: "1px solid #EAECF0",
      borderRadius: "10px",
      backgroundColor: "#ffffff",
    }}
  >
    <Typography sx={{ fontFamily: "Inter", fontSize: "12px", fontWeight: 500, color: "#667085" }}>{label}</Typography>
    <Typography
      sx={{
        mt: 0.25,
        fontFamily: "Inter",
        fontSize: "20px",
        fontWeight: 700,
        color,
        fontVariantNumeric: "tabular-nums",
      }}
    >
      {value === null ? <Skeleton width={110} /> : formatINR(value)}
    </Typography>
    {hint && <Typography sx={{ fontFamily: "Inter", fontSize: "11.5px", color: "#667085" }}>{hint}</Typography>}
  </Box>
);

const SkeletonRows = () =>
  Array.from({ length: 5 }).map((_, row) => (
    <TableRow key={row}>
      {COLUMNS.map((label) => (
        <TableCell key={label} className={Styles.bodyCell}>
          <Skeleton variant="text" width="80%" />
        </TableCell>
      ))}
    </TableRow>
  ));

/**
 * The money picture of one wallet: four figures, a warning when it does not add up, and the ledger
 * (newest first, paged on the server). `fetchStatement({ page, limit })` (page is 1-based) answers like
 * the statement endpoints. All state lives here, so mount it again (a new `key`) to start over.
 * `onOpenPolicy(policyId)`, when given, turns the policy line of a debit or refund into a link; the parent decides
 * who may open policies and how (this view has no router of its own).
 */
const WalletStatementView = ({ fetchStatement, audience = "admin", onLoaded, onOpenPolicy }) => {
  const copy = COPY[audience] || COPY.admin;
  const [page, setPage] = useState(0); // 0-based for the pagination control; the API is 1-based
  const [limit, setLimit] = useState(25);
  const [reloadKey, setReloadKey] = useState(0);
  const [figures, setFigures] = useState(null);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [error, setError] = useState("");
  const latestRequest = useRef(0);
  // Always the latest callbacks, without making the load effect depend on them.
  const callbacks = useRef({});
  callbacks.current = { fetchStatement, onLoaded };

  useEffect(() => {
    const id = ++latestRequest.current;
    setLoading(true);
    setError("");
    callbacks.current.fetchStatement({ page: page + 1, limit }).then((res) => {
      if (id !== latestRequest.current) return; // a newer request (or an unmount) superseded this one
      setLoading(false);
      if (res.error) {
        setError(res.message || "Could not load the statement");
        return;
      }
      const body = res.data || {};
      setFigures(body);
      setRows(Array.isArray(body.rows) ? body.rows : []);
      setTotal(Number(body.total) || 0);
      setHasLoaded(true);
      if (callbacks.current.onLoaded) callbacks.current.onLoaded(body);
    });
    return () => {
      latestRequest.current += 1;
    };
  }, [page, limit, reloadKey]);

  const showSkeleton = !hasLoaded && loading;
  const showEmpty = !error && hasLoaded && !loading && rows.length === 0;
  const warnings = audience === "user" ? [userWalletNotice(figures)].filter(Boolean) : walletWarnings(figures);
  const figure = (key) => (figures ? figures[key] : null);

  return (
    <>
      <Box sx={{ p: "22px", pb: 1.5, display: "flex", flexDirection: "column", gap: "14px" }}>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
          <FigureCard label={copy.balanceLabel} value={figure("balance")} color="#1E3A5F" hint={copy.balanceHint} />
          <FigureCard
            label={copy.pendingLabel}
            value={figure("pendingAmount")}
            color="#B54708"
            hint={
              figures
                ? `${figures.pendingCount} request${figures.pendingCount === 1 ? "" : "s"} awaiting approval`
                : undefined
            }
          />
          <FigureCard
            label="Total Added"
            value={figure("totalAdded")}
            color="#12762D"
            hint="Approved top-ups + added by admin"
          />
          <FigureCard label="Total Used" value={figure("totalUsed")} hint="Policy debits minus refunds" />
        </Box>
        <Typography sx={{ fontFamily: "Inter", fontSize: "11.5px", color: "#667085" }}>
          Balance = Total Added − Total Used. Pending money is not part of the balance until it is approved.
        </Typography>

        {warnings.length > 0 && (
          <Box
            role="alert"
            sx={{
              display: "flex",
              gap: 1.25,
              p: 1.5,
              border: "1px solid #FEDF89",
              borderRadius: "8px",
              backgroundColor: "#FFFAEB",
            }}
          >
            <WarningAmberIcon sx={{ fontSize: 20, color: "#B54708", mt: "1px" }} />
            <Box>
              {warnings.map((text) => (
                <Typography key={text} sx={{ fontFamily: "Inter", fontSize: "12.5px", color: "#93370D" }}>
                  {text}
                </Typography>
              ))}
              {audience !== "user" && (
                <Typography sx={{ fontFamily: "Inter", fontSize: "12px", color: "#93370D", mt: 0.5 }}>
                  Please check with the developer before relying on this balance.
                </Typography>
              )}
            </Box>
          </Box>
        )}
      </Box>

      {error && (
        <Box sx={{ px: 2.75, pb: 2 }}>
          <Box
            role="alert"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              p: 1.5,
              border: "1px solid #f3d6d6",
              borderRadius: "8px",
              backgroundColor: "#fdf7f7",
            }}
          >
            <ErrorOutlineIcon sx={{ fontSize: 20, color: "#b3261e" }} />
            <Typography sx={{ flex: 1, fontFamily: "Inter", fontSize: "12.5px", color: "#b3261e" }}>{error}</Typography>
            <Button size="small" variant="outlined" onClick={() => setReloadKey((n) => n + 1)} sx={outlinedButtonSx}>
              Retry
            </Button>
          </Box>
        </Box>
      )}

      {showEmpty && (
        <Box className={Styles.EmptyState}>
          <img src={NoData} alt="" style={{ width: 200, height: 133, objectFit: "contain" }} />
          <Typography sx={{ color: "#1E3A5F", fontFamily: "Inter", fontSize: "16px", fontWeight: 600 }}>
            No transactions yet
          </Typography>
          <Typography sx={{ color: "#667085", fontFamily: "Inter", fontSize: "12.5px" }}>{copy.emptyText}</Typography>
        </Box>
      )}

      {!error && !showEmpty && (
        <Box className={Styles.tableWrap}>
          <Box sx={{ height: 3 }}>{loading && !showSkeleton && <LinearProgress sx={{ height: 3 }} />}</Box>
          <Box className={Styles.tableScroll}>
            <Table aria-label="Wallet statement" sx={{ minWidth: 860 }}>
              <TableHead>
                <TableRow>
                  {COLUMNS.map((label) => (
                    <TableCell
                      key={label}
                      className={Styles.headCell}
                      align={label === "Amount" || label === "Balance after" ? "right" : "left"}
                    >
                      {label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody className={loading && !showSkeleton ? Styles.dim : ""}>
                {showSkeleton ? (
                  <SkeletonRows />
                ) : (
                  rows.map((row) => {
                    const details = describeRow(row);
                    const signed = Number(row.signedAmount) || 0;
                    return (
                      <TableRow key={row._id} className={Styles.row}>
                        <TableCell className={Styles.bodyCell} sx={{ whiteSpace: "nowrap" }}>
                          {row.createdAt ? moment(row.createdAt).format("DD/MM/YYYY hh:mm A") : <Dash />}
                        </TableCell>
                        <TableCell className={Styles.bodyCell}>
                          <TypeChip type={row.transactionType} />
                        </TableCell>
                        <TableCell className={Styles.bodyCell} sx={{ maxWidth: 360 }}>
                          <Box
                            sx={{
                              fontWeight: 500,
                              color: details.muted ? "#98A2B3" : "#1E3A5F",
                              overflowWrap: "anywhere",
                            }}
                          >
                            {!details.primary ? (
                              <Dash />
                            ) : onOpenPolicy && canOpenPolicy(row) ? (
                              <Link
                                component="button"
                                type="button"
                                underline="hover"
                                onClick={() => onOpenPolicy(row.referenceId)}
                                aria-label={`Open policy ${row.policy.policyId || ""}`.trim()}
                                sx={{ font: "inherit", fontWeight: 500, color: "#1E3A5F", textAlign: "left" }}
                              >
                                {details.primary}
                              </Link>
                            ) : (
                              details.primary
                            )}
                          </Box>
                          {details.secondary && (
                            <Box sx={{ fontSize: "12px", color: "#667085", overflowWrap: "anywhere" }}>
                              {details.secondary}
                            </Box>
                          )}
                          {details.tertiary && (
                            <Box sx={{ fontSize: "11.5px", color: "#98A2B3", overflowWrap: "anywhere" }}>
                              {details.tertiary}
                            </Box>
                          )}
                        </TableCell>
                        <TableCell
                          align="right"
                          className={Styles.bodyCell}
                          sx={{
                            whiteSpace: "nowrap",
                            fontWeight: 600,
                            fontVariantNumeric: "tabular-nums",
                            color: `${signed < 0 ? "#B42318" : "#12762D"} !important`,
                          }}
                        >
                          {formatSigned(signed)}
                        </TableCell>
                        <TableCell
                          align="right"
                          className={Styles.bodyCell}
                          sx={{ whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}
                        >
                          {row.balanceAfter === null || row.balanceAfter === undefined ? (
                            <Dash />
                          ) : (
                            formatINR(row.balanceAfter)
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </Box>
          <Box className={Styles.pagination}>
            <TablePagination
              component="div"
              rowsPerPageOptions={PAGE_SIZES}
              count={total}
              rowsPerPage={limit}
              page={page}
              onPageChange={(_, next) => setPage(next)}
              onRowsPerPageChange={(event) => {
                setLimit(parseInt(event.target.value, 10));
                setPage(0);
              }}
            />
          </Box>
        </Box>
      )}
    </>
  );
};

export default WalletStatementView;
